/*#
# human-proxy

A [human pack](/human-pack/)'s clothes, hair and brows on a built body:
fitted by [proxy-fit](/proxy-fit/)'s binding (so they follow every body
shape), skinned to the body's own skeleton with weights borrowed from the
skin they are bound to (so they bend with it), and drawn with their own
textures. `coverBody` hides the skin a garment covers, so it cannot poke
through.

The [character builder](/character-builder/) dresses its walker this way:
MakeHuman's CC0 casual, work and formal suits, shoes, a fedora, and its hair
and brows beside Quaternius'.
*/
/*{ "parent": "character-builder", "order": 66 }*/

import * as BABYLON from '@babylonjs/core'
import type { HumanProxy } from './human-pack.js'
import type { BuiltRig } from './rig-build.js'
import { coverSkin } from './proxy-cover.js'
import {
  coveredTriangles,
  drawnPositions,
  fitProxy,
  inflate,
  proxyWeights,
  type VertexWeights,
} from './proxy-fit.js'

/**
 * A material for a proxy from its own textures, or one of its alternate
 * `look`s (colourways; `proxy.material.looks`).
 */
export function proxyMaterial(
  scene: BABYLON.Scene,
  proxy: HumanProxy,
  look?: string
): BABYLON.PBRMaterial {
  const alt = look != null ? proxy.material.looks?.[look] : undefined
  const textures = alt ?? proxy.material
  const m = new BABYLON.PBRMaterial(
    `proxy-${proxy.name}${alt ? `-${look}` : ''}`,
    scene
  )
  m.metallic = 0
  // Hair is many fine strands, which scatter light: a broad, soft highlight,
  // not a mirror. At 0.45 under an environment it shone like wet plastic.
  m.roughness = proxy.category === 'clothes' ? 0.8 : 0.72
  if (proxy.category !== 'clothes') m.environmentIntensity = 0.5
  if (textures.diffuse) {
    // OBJ's V runs up, as an image loaded with Babylon's default flip expects.
    m.albedoTexture = new BABYLON.Texture(textures.diffuse, scene)
  }
  if (textures.normal ?? proxy.material.normal) {
    m.bumpTexture = new BABYLON.Texture(
      (textures.normal ?? proxy.material.normal)!,
      scene
    )
  }
  if (proxy.material.alpha && m.albedoTexture) {
    m.albedoTexture.hasAlpha = true
    m.useAlphaFromAlbedoTexture = true
    // Lenses are glass, partly see-through: blended. Hair cards are cut out.
    if (proxy.slot === 'glasses') {
      m.transparencyMode = BABYLON.PBRMaterial.PBRMATERIAL_ALPHABLEND
    } else {
      m.transparencyMode = BABYLON.PBRMaterial.PBRMATERIAL_ALPHATEST
      m.alphaCutOff = 0.4
    }
  }
  // What a garment from a file brings (garment-file).
  const pm = proxy.material
  if (pm.roughness != null) m.roughness = pm.roughness
  if (pm.metallic != null) m.metallic = pm.metallic
  if (pm.baseColor) {
    const [r, g, b, a] = pm.baseColor
    m.albedoColor = new BABYLON.Color3(r, g, b)
    m.alpha = a
  }
  if (pm.metallicRoughness) {
    m.metallicTexture = new BABYLON.Texture(pm.metallicRoughness, scene)
    m.useRoughnessFromMetallicTextureGreen = true
    m.useMetallnessFromMetallicTextureBlue = true
    m.useRoughnessFromMetallicTextureAlpha = false
  }
  if (m.bumpTexture && pm.normalScale != null)
    m.bumpTexture.level = pm.normalScale
  if (pm.baseColor) {
    // glTF's normal maps are OpenGL's (green up), on a mesh mirrored into
    // Babylon's left-handed scene, with V flipped to the OBJ convention.
    m.invertNormalMapX = true
    m.invertNormalMapY = true
  }
  if (pm.blend) m.transparencyMode = BABYLON.PBRMaterial.PBRMATERIAL_ALPHABLEND
  if (pm.alphaCutoff != null) m.alphaCutOff = pm.alphaCutoff
  m.backFaceCulling = !proxy.material.twoSided
  // The inside of a skirt is lit as an inside, not as the outside's back.
  m.twoSidedLighting = proxy.material.twoSided === true
  return m
}

/**
 * A proxy on a built body. `positions` are the morphed pack positions the
 * body was built from (every vertex); `weights` has skin weights for every
 * base vertex (`nearestWeights`).
 */
export function humanProxy(
  scene: BABYLON.Scene,
  built: BuiltRig,
  proxy: HumanProxy,
  positions: ArrayLike<number>,
  weights: VertexWeights,
  material?: BABYLON.Material,
  options: {
    /** Push it out from a point (a hat over hair, from the skull's middle). */
    inflate?: { centre: { x: number; y: number; z: number }; by: number }
    /** Push it out along its own surface by metres (armour over a bodysuit). */
    lift?: number
  } = {}
): BABYLON.Mesh {
  const drawn = drawnPositions(proxy, fitProxy(proxy, positions))
  if (options.inflate)
    inflate(drawn, options.inflate.centre, options.inflate.by)
  const mesh = new BABYLON.Mesh(`proxy-${proxy.name}`, scene)
  const data = new BABYLON.VertexData()
  data.positions = drawn
  data.indices = Array.from(proxy.triangles)
  data.uvs = proxy.uvs
  const normals: number[] = []
  // Counter-clockwise, outward: the file's handedness, under the rig's mirror.
  BABYLON.VertexData.ComputeNormals(drawn, data.indices, normals, {
    useRightHandedSystem: true,
  })
  data.normals = normals
  if (options.lift) {
    for (let i = 0; i < drawn.length; i++) drawn[i] += normals[i] * options.lift
  }
  data.applyToMesh(mesh)
  mesh.sideOrientation = BABYLON.Material.ClockWiseSideOrientation
  const w = proxyWeights(proxy, weights)
  mesh.setVerticesData(BABYLON.VertexBuffer.MatricesIndicesKind, w.indices)
  mesh.setVerticesData(BABYLON.VertexBuffer.MatricesWeightsKind, w.weights)
  mesh.skeleton = built.skeleton
  mesh.parent = built.root
  mesh.material = material ?? proxyMaterial(scene, proxy)
  return mesh
}

/**
 * Hide the body's skin under what it wears. Triangles wholly under a garment
 * are dropped, and the skin it covers (listed, or found by `coveredBy` from
 * `drawn`) is TUCKED: drawn in along its normal by
 * `tuck` metres, so where a garment's edge and its listed vertices disagree
 * the skin dips under the edge instead of leaving a hole. `triangles` and
 * `vertices` are the body's compact skin (`humanSkin`).
 */
export function coverBody(
  built: BuiltRig,
  triangles: ArrayLike<number>,
  vertices: ArrayLike<number>,
  wearing: (HumanProxy | null)[],
  options: {
    tuck?: number
    /**
     * The worn garments' drawn positions (by proxy name, the pack's frame),
     * so skin they cover but do not list is found by geometry and tucked too.
     */
    drawn?: Map<string, Float32Array>
    /** Metres over which a tuck fades in from the covered skin's edge. */
    fade?: number
  } = {}
): void {
  const mesh = built.mesh
  // The untucked shape, kept from the first call (tucks must not accumulate).
  const meta = (mesh.metadata ??= {}) as {
    untucked?: Float32Array
    normals?: Float32Array
  }
  if (meta.untucked == null) {
    meta.untucked = Float32Array.from(
      mesh.getVerticesData(BABYLON.VertexBuffer.PositionKind)!
    )
    meta.normals = Float32Array.from(
      mesh.getVerticesData(BABYLON.VertexBuffer.NormalKind)!
    )
  }
  const { positions, deleted } = coverSkin(
    meta.untucked,
    meta.normals!,
    triangles,
    vertices,
    wearing,
    options
  )
  // set, not update: an update to a buffer made without `updatable` is a
  // silent no-op in Babylon.
  mesh.setVerticesData(BABYLON.VertexBuffer.PositionKind, positions, true)
  // Triangles are dropped only when a garment lists skin to delete; setting
  // indices makes one sub-mesh, so a body drawn in several materials (a
  // file's) keeps its own when nothing is dropped.
  const cut = meta as { dropped?: boolean }
  if (deleted.size === 0 && !cut.dropped) return
  cut.dropped = deleted.size > 0
  mesh.setIndices(
    deleted.size
      ? coveredTriangles(triangles, vertices, deleted, 'all')
      : Array.from(triangles)
  )
}
